import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { chatThreadLabel } from "@/lib/rd-page-tabs";
import { cn } from "@/lib/utils";
import { formatRelativeShort } from "../../../../shared/local-date";
import type {
  WorkspaceOtherOwnerThreads,
  WorkspaceThreadSummary,
} from "../../../../shared/api-types";

// The classic RD workspace sidebar's navigation rows and its Chats and other
// supervisors' chats groups (NO-754), moved out of rd-workspace-sidebar.tsx
// verbatim, so the classic DOM is unchanged. The single-rail frame lists the
// viewer's threads in the rail (always-rail-chats.tsx) and the RD page's Chats
// card (rd-chats-card.tsx) instead.

const VISIBLE_THREADS = 5;

/**
 * TanStack Router's <Link> is statically typed against the route tree, so the
 * NavRow accepts a discriminated `{ to, params }` shape (record surfaces vs.
 * chat thread) rather than the legacy plain `href` string.
 */
export type NavRowLink =
  | {
      to:
        | "/rds/$rdId"
        | "/rds/$rdId/scorecards"
        | "/rds/$rdId/metrics"
        | "/rds/$rdId/meetings"
        | "/rds/$rdId/observations"
        | "/rds/$rdId/forms"
        | "/rds/$rdId/chats/new";
      params: { rdId: string };
    }
  | {
      to: "/rds/$rdId/chats/$chatId";
      params: { rdId: string; chatId: string };
    };

export function NavRow({
  active,
  children,
  meta,
  className,
  ...link
}: NavRowLink & {
  active: boolean;
  children: ReactNode;
  meta?: string;
  className?: string;
}) {
  return (
    <Link
      {...link}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex min-h-[32px] items-center justify-between gap-2 rounded-lg px-[10px] py-[5px] text-body-lg font-medium leading-[1.4] transition-colors",
        active
          ? "bg-accent font-semibold text-foreground"
          : "text-sand-600 hover:bg-bark/[0.04]",
        className
      )}
    >
      <span className="truncate">{children}</span>
      {meta !== undefined && (
        <span className="shrink-0 text-xs text-subtle">{meta}</span>
      )}
    </Link>
  );
}

export function NavHeading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1 mt-4 px-[10px] font-sans text-caption font-extrabold uppercase tracking-[0.08em] text-subtle first:mt-[6px]">
      {children}
    </p>
  );
}

function ThreadRows({
  threads,
  rdSlug,
  pathname,
}: {
  threads: WorkspaceThreadSummary[];
  rdSlug: string;
  pathname: string;
}) {
  return (
    <>
      {threads.map((thread) => (
        <NavRow
          key={thread.chatId}
          to="/rds/$rdId/chats/$chatId"
          params={{ rdId: rdSlug, chatId: thread.chatId }}
          active={pathname === `/rds/${rdSlug}/chats/${thread.chatId}`}
          meta={formatRelativeShort(thread.updatedAt)}
        >
          {chatThreadLabel(thread)}
        </NavRow>
      ))}
    </>
  );
}

export function ChatsSection({
  threads,
  rdSlug,
  pathname,
}: {
  threads: WorkspaceThreadSummary[];
  rdSlug: string;
  pathname: string;
}) {
  const [showAllThreads, setShowAllThreads] = useState(false);
  const visibleThreads = showAllThreads ? threads : threads.slice(0, VISIBLE_THREADS);
  const hiddenCount = threads.length - VISIBLE_THREADS;

  return (
    <section>
      <NavHeading>Chats</NavHeading>
      <div className="flex flex-col gap-[1px]">
        <NavRow
          to="/rds/$rdId/chats/new"
          params={{ rdId: rdSlug }}
          active={pathname === `/rds/${rdSlug}/chats/new`}
        >
          <span className="font-semibold text-primary">New chat</span>
        </NavRow>
        <ThreadRows threads={visibleThreads} rdSlug={rdSlug} pathname={pathname} />
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAllThreads((v) => !v)}
            className="mt-[1px] min-h-[30px] rounded-lg px-[10px] text-left text-body-sm font-medium text-sand-400 hover:bg-bark/[0.04] hover:text-sand-500"
          >
            {showAllThreads ? "Show fewer" : `Show ${hiddenCount} more`}
          </button>
        )}
      </div>
    </section>
  );
}

export function OtherOwnersSection({
  otherOwners,
  rdSlug,
  pathname,
}: {
  otherOwners: WorkspaceOtherOwnerThreads[];
  rdSlug: string;
  pathname: string;
}) {
  if (otherOwners.length === 0) return null;

  return (
    <section>
      <NavHeading>Other supervisors&rsquo; chats</NavHeading>
      {otherOwners.map((owner) => (
        <div key={owner.ownerUserId} className="mb-1">
          <p className="truncate px-[10px] pb-[2px] pt-1 text-xs font-medium text-muted-foreground">
            {owner.ownerName}
          </p>
          <div className="flex flex-col gap-[1px]">
            <ThreadRows threads={owner.threads} rdSlug={rdSlug} pathname={pathname} />
          </div>
        </div>
      ))}
    </section>
  );
}
